import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "../components/SEO";
import api from "../api/axios"; // NEW
import { toast } from "react-hot-toast";
import { Mail, Lock, ArrowRight, Sparkles, ShieldCheck, KeyRound } from "lucide-react";
import logoLight from "../assets/antaryatra-logo-horizontal 3.0.svg";
import { useAuth } from "../context/AuthContext"; // Use Context
import { useLanguage } from "../context/LanguageContext";
import tracker from "../utils/sanctuary-tracker";

const QUOTES = [
  { text: "Peace comes from within. Do not seek it without.", author: "Buddha" },
  { text: "The wound is the place where the Light enters you.", author: "Rumi" },
  { text: "Knowing others is wisdom, knowing yourself is enlightenment.", author: "Lao Tzu" },
  { text: "What you seek is seeking you.", author: "Rumi" },
  { text: "The journey of a thousand miles begins with a single step.", author: "Lao Tzu" }
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth(); // Hook
  const { t } = useLanguage();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState(""); // NEW
  
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const [quoteIndex, setQuoteIndex] = useState(0);

  // Rotate quotes every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % QUOTES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const validateEmail = (val) => {
    // Simple regex for inline checking
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val) {
      setEmailError("Email is required");
      return false;
    }
    if (!regex.test(val)) {
      setEmailError("That email doesn't look quite right.");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateEmail(email)) return;

    setLoading(true);

    try {
      // POST /auth/login -> browser sets cookie automatically
      const { data } = await api.post("/auth/login", { email, password });

      if (data.requires2FA) {
        setRequires2FA(true);
        setTempToken(data.tempToken);
        toast.success("Please enter your Authenticator code");
        setLoading(false);
        return;
      }

      // Update Context
      await login(data);

      toast.success("Welcome back!");
      tracker.track('login_success', { method: 'email' });

      // Redirect based on role
      if (data.user?.role === "ADMIN" || data.role === "ADMIN") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Invalid credentials. Please try again.");
    } finally {
      if (!requires2FA) {
        setLoading(false);
      }
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!otpCode || otpCode.length < 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post("/auth/verify-2fa", { tempToken, code: otpCode });
      
      await login(data);
      toast.success("Verification successful!");
      tracker.track('login_success', { method: '2fa' });
      
      if (data.user?.role === "ADMIN" || data.role === "ADMIN") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Invalid authenticator code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-serenity-blue-50 to-serenity-purple-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title="Welcome Back" 
        description="Log in to your Antaryatra sanctuary. Your encrypted and secure space for emotional reflection." 
        url="/login" 
      />
      {/* LEFT PANEL: Cosmos / Starfield */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0a0a1a 0%, #0f0c29 40%, #1a133a 70%, #0d0d2b 100%)" }}>

        {/* ── Star field (pure CSS, no library) ── */}
        {[
          [8, 12, 0.9, 1.2], [15, 38, 0.6, 0.8], [22, 7, 0.8, 1.0], [31, 55, 0.5, 0.9], [40, 22, 0.7, 1.1],
          [48, 73, 0.6, 0.8], [55, 14, 0.9, 1.3], [63, 87, 0.5, 0.7], [70, 42, 0.8, 1.0], [78, 6, 0.6, 0.9],
          [85, 67, 0.7, 1.1], [92, 31, 0.5, 0.8], [5, 80, 0.6, 1.0], [12, 55, 0.8, 1.2], [20, 90, 0.5, 0.7],
          [27, 17, 0.9, 1.1], [35, 74, 0.6, 0.9], [43, 48, 0.7, 1.0], [51, 92, 0.5, 0.8], [59, 5, 0.8, 1.2],
          [67, 61, 0.6, 0.9], [75, 28, 0.7, 1.1], [83, 83, 0.5, 0.8], [90, 50, 0.9, 1.3], [3, 44, 0.6, 0.9],
          [10, 19, 0.8, 1.0], [18, 66, 0.5, 0.7], [26, 33, 0.7, 1.1], [34, 88, 0.6, 0.9], [42, 9, 0.9, 1.2],
          [50, 57, 0.5, 0.8], [58, 78, 0.8, 1.0], [66, 24, 0.6, 0.9], [74, 95, 0.7, 1.1], [82, 40, 0.5, 0.8],
          [89, 16, 0.9, 1.3], [7, 96, 0.6, 0.9], [14, 71, 0.8, 1.0], [21, 47, 0.5, 0.7], [29, 84, 0.7, 1.1],
          [37, 3, 0.6, 0.9], [45, 62, 0.9, 1.2], [53, 37, 0.5, 0.8], [61, 10, 0.8, 1.0], [69, 79, 0.6, 0.9],
          [77, 53, 0.7, 1.1], [85, 21, 0.5, 0.8], [93, 88, 0.9, 1.3], [2, 28, 0.6, 0.9], [16, 85, 0.7, 1.0],
          [24, 60, 0.5, 0.7], [32, 15, 0.8, 1.1], [44, 40, 0.6, 0.9], [60, 93, 0.7, 1.0], [72, 68, 0.5, 0.8],
        ].map(([x, y, op, size], i) => (
          <div key={i} className="absolute rounded-full bg-white" style={{
            left: `${x}%`, top: `${y}%`,
            width: `${size * 2}px`, height: `${size * 2}px`,
            opacity: op,
            animation: `pulse ${2 + (i % 3)}s ease-in-out infinite alternate`,
            animationDelay: `${(i * 0.3) % 3}s`,
          }} />
        ))}

        {/* ── Radial glow ── */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 60% 50% at 50% 55%, rgba(99,102,241,0.18) 0%, transparent 70%)",
        }} />

        {/* ── Top-left brand mark ── */}
        <div className="relative z-10 flex items-center gap-3">
          <img src={logoLight} alt="Antaryatra" className="h-10 w-auto object-contain" />
        </div>

        {/* ── Centre hero copy ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="relative z-10 flex flex-col justify-center flex-1 py-12"
        >
          <h1 className="text-5xl font-display font-bold text-white leading-[1.15] mb-4">
            {t('auth_login_welcome')}
          </h1>
          <p className="text-indigo-200/70 text-lg font-light leading-relaxed max-w-xs">
            The bravest thing you did today was show up.
          </p>
        </motion.div>

        {/* ── Rotating quote as pull-quote ── */}
        <div className="relative z-10 h-28">
          <AnimatePresence mode="wait">
            <motion.div
              key={quoteIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 flex flex-col justify-end"
            >
              <div className="border-l-2 border-indigo-400/50 pl-4">
                <p className="text-indigo-100/80 text-sm leading-relaxed italic">
                  {QUOTES[quoteIndex].text}
                </p>
                <p className="mt-2 text-indigo-300/60 text-xs font-semibold tracking-wide">
                  — {QUOTES[quoteIndex].author}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── CSS pulse keyframe ── */}
        <style>{`
          @keyframes pulse {
            from { opacity: 0.3; transform: scale(0.9); }
            to   { opacity: 1;   transform: scale(1.1); }
          }
        `}</style>
      </div>

      {/* RIGHT PANEL: Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md space-y-8"
        >

          {/* Header */}
          <div className="space-y-2">
            <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
              {t('auth_login_btn')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              {t('auth_login_prompt')}
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100 flex items-start gap-2"
            >
              <span className="mt-0.5 block w-2 h-2 rounded-full bg-red-500 shrink-0" />
              {error}
            </motion.div>
          )}

          {requires2FA ? (
            <form onSubmit={handleVerify2FA} className="space-y-5">
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-blue transition-colors">
                  <KeyRound className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="6-digit Authenticator Code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  disabled={loading}
                  required
                  className="w-full px-4 py-3 pl-12 text-center tracking-widest text-xl font-mono bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue focus:outline-none transition-all placeholder:text-gray-400 placeholder:text-base placeholder:font-sans placeholder:tracking-normal"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    {t('auth_2fa_verify_btn')}
                  </>
                )}
              </button>
              
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setRequires2FA(false);
                    setOtpCode("");
                    setTempToken("");
                    setError("");
                  }}
                  className="text-sm font-medium text-gray-500 hover:text-brand-blue transition-colors"
                >
                  {t('auth_2fa_back_link')}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-blue transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={() => validateEmail(email)}
                  disabled={loading}
                  required
                  className={`w-full px-4 py-3 pl-12 bg-white dark:bg-gray-800 border rounded-xl focus:ring-2 focus:outline-none transition-all placeholder:text-gray-400 ${emailError
                    ? "border-red-300 focus:ring-red-200 focus:border-red-500"
                    : "border-gray-200 dark:border-gray-700 focus:ring-brand-blue focus:border-brand-blue"
                    }`}
                />
              </div>
              {emailError && (
                <p className="text-sm text-red-500 mt-1 pl-1">{emailError}</p>
              )}

              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-blue transition-colors">
                  <Lock className="w-5 h-5" />
                </div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  className="w-full px-4 py-3 pl-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-brand-blue focus:outline-none transition-all placeholder:text-gray-400"
                />
              </div>

              <div className="flex items-center justify-end">
                <Link to="/forgot-password" university className="text-sm font-medium text-brand-blue hover:text-brand-purple transition-colors">
                  {t('auth_recovery_link')}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : t('auth_login_btn')}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>

              <div className="flex items-center gap-4 my-6">
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                <span className="text-xs text-gray-400 uppercase tracking-widest font-medium">or</span>
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              </div>

              <button 
                type="button" 
                onClick={() => {
                  window.location.href = `${api.defaults.baseURL}/auth/google`;
                }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group"
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Continue with Google</span>
              </button>
            </form>
          )}

          <p className="text-center text-sm text-gray-500 mt-8">
            {t('auth_new_text')}{" "}
            <button
              onClick={() => {
                navigate("/signup");
              }}
              className="text-brand-blue font-semibold hover:underline bg-transparent border-none p-0 cursor-pointer"
            >
              {t('auth_new_link')}
            </button>
          </p>

        </motion.div>
      </div>
    </div>
  );
}
